import { ConfiguratorGroupDto, ConfiguratorOptionDto } from '@/services/configurator';

/** The group's applied selection — the explicit `selectedOption`, falling back to a flagged option. */
export const groupSelectedOption = (group: ConfiguratorGroupDto): ConfiguratorOptionDto | null =>
    group.selectedOption ?? group.options.find((o) => o.selected) ?? null;

/**
 * The unit for a group's quantity (e.g. "m²"). The group DTO carries no unit of its own — it comes
 * from the group's pricing field, surfaced on each option's price as `unitAbbreviation` (identical
 * across the group's options). Read it off any priced option; null when there's no unit.
 */
export const groupQuantityUnit = (group: ConfiguratorGroupDto): string | null =>
    group.options.find((o) => o.price?.unitAbbreviation)?.price?.unitAbbreviation ?? null;

// The configurator API returns each group's options in a project-defined order, but a refetch after
// a selection change (e.g. confirming the dependency dialog) can come back in a different order,
// which shuffles the option menu. Options carry no `sortOrder` to sort by, so we remember the order
// each option first appeared in — seeded from the full group list on load, before any mutation — and
// re-impose it. Module-level so it survives GroupOptionDetail unmounting when switching groups.
const firstSeenOptionOrder = new Map<number, Map<number, number>>();

const optionOrderFor = (groupId: number): Map<number, number> => {
    let order = firstSeenOptionOrder.get(groupId);
    if (!order) {
        order = new Map();
        firstSeenOptionOrder.set(groupId, order);
    }
    return order;
};

/**
 * Record the first-seen index of every not-yet-recorded option, per group. Pass the FULL group list
 * (all deadlines) so groups the buyer hasn't opened yet are captured in their pre-defined order
 * before any mutation can reorder them. Idempotent: options already recorded keep their index.
 */
export const registerPredefinedOptionOrder = (groups: ConfiguratorGroupDto[]): void => {
    for (const group of groups) {
        const order = optionOrderFor(group.id);
        for (const option of group.options) {
            if (!order.has(option.id)) order.set(option.id, order.size);
        }
    }
};

/** Re-sort a group's options into their first-seen (pre-defined) order. */
export const inPredefinedOptionOrder = (
    groupId: number,
    options: ConfiguratorOptionDto[]
): ConfiguratorOptionDto[] => {
    const order = optionOrderFor(groupId);
    // Any option not seen at seed time (rare) is appended after the known ones, in its current order.
    for (const option of options) {
        if (!order.has(option.id)) order.set(option.id, order.size);
    }
    return [...options].sort((a, b) => order.get(a.id)! - order.get(b.id)!);
};

/** Map of every group's currently-selected option id, keyed by group id (groups with no pick omitted). */
export const buildSelectedOptionIdByGroup = (groups: ConfiguratorGroupDto[]): Map<number, number> => {
    const selectedByGroup = new Map<number, number>();
    for (const group of groups) {
        const selected = groupSelectedOption(group);
        if (selected) selectedByGroup.set(group.id, selected.id);
    }
    return selectedByGroup;
};

/**
 * Split a group's options into dependency-compatible (valid) and conflicting (invalid) buckets,
 * relative to the current selections in the OTHER groups. An option is invalid when one of its
 * `dependencyConflicts` points at an option currently selected in a *different* group — the same
 * rule the dependency wizard uses.
 *
 * Conflicts referencing `currentGroupId` are ignored: a single-select group's options list their
 * own siblings as conflicts (picking one excludes the others), so counting the group's own
 * selection would wrongly mark every alternative invalid. `findDependentGroups` excludes the parent
 * group for the same reason.
 */
export const partitionOptionsByCompatibility = (
    options: ConfiguratorOptionDto[],
    selectedOptionIdByGroup: Map<number, number>,
    currentGroupId: number
) => {
    const isIncompatible = (option: ConfiguratorOptionDto) =>
        (option.dependencyConflicts ?? []).some(
            (conflict) =>
                conflict.groupId !== currentGroupId &&
                selectedOptionIdByGroup.get(conflict.groupId) === conflict.optionId
        );
    return {
        valid: options.filter((o) => !isIncompatible(o)),
        invalid: options.filter(isIncompatible),
    };
};
